import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchAuthSession } from '@aws-amplify/auth';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import Loader from '../../components/loader/Loader';
import StatCard from '../../components/dashboard/StatCard';
import ChartCard from '../../components/dashboard/ChartCard';
import DashboardTabs from '../../components/dashboard/DashboardTabs';
import DashboardFilters from '../../components/dashboard/DashboardFilters';
import { baseOptions, doughnutOptions, CHART } from '../../components/dashboard/chartTheme';
import { formatCurrency, formatNumber, formatDate, costTone } from '../../components/dashboard/format';
import {
  fetchCostOverview,
  fetchCostUser,
  fetchCostAnalytics,
} from '../../api/costTrackerApi';
import styles from '../../components/dashboard/Dashboard.module.css';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'analytics', label: 'Analytics' },
];

const isoDaysAgo = (days) =>
  new Date(Date.now() - days * 86400000).toISOString().split('T')[0];

function CostTracker() {
  const navigate = useNavigate();
  const [authorized, setAuthorized] = useState(false);
  const [phone, setPhone] = useState('');
  const [startDate, setStartDate] = useState(isoDaysAgo(30));
  const [endDate, setEndDate] = useState(isoDaysAgo(0));
  const [activeTab, setActiveTab] = useState('overview');
  const [data, setData] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userLimit, setUserLimit] = useState(10);

  useEffect(() => {
    (async () => {
      try {
        const session = await fetchAuthSession();
        if (session.credentials) setAuthorized(true);
        else navigate('/admin/login');
      } catch (err) {
        navigate('/admin/login');
      }
    })();
  }, [navigate]);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const range = { startDate, endDate };
      const cleanPhone = phone.trim();
      const [overviewRes, analyticsRes] = await Promise.allSettled([
        cleanPhone
          ? fetchCostUser(cleanPhone, range)
          : fetchCostOverview(range),
        fetchCostAnalytics(range),
      ]);
      if (overviewRes.status === 'rejected') throw overviewRes.reason;
      const overview = overviewRes.value;
      if (overview.error) throw new Error(overview.error);
      setData(overview);
      setAnalytics(analyticsRes.status === 'fulfilled' ? analyticsRes.value : null);
    } catch (err) {
      setError(`Error loading dashboard: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [phone, startDate, endDate]);

  useEffect(() => {
    if (authorized) loadDashboard();
  }, [authorized]);

  const summary = data?.summary || {};
  const dailyData = data?.dailyData || [];

  const costTrend = {
    labels: dailyData.map((d) => formatDate(d.date)),
    datasets: [
      {
        label: 'Daily Cost ($)',
        data: dailyData.map((d) => d.cost),
        borderColor: CHART.primary,
        backgroundColor: 'rgba(1, 105, 235, 0.1)',
        tension: 0.4,
        fill: true,
        pointRadius: 4,
      },
    ],
  };

  const hourly = data?.hourlyPatterns || [];
  const hourlyMap = {};
  hourly.forEach((h) => (hourlyMap[h.hour] = h));
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const hourlyChart = {
    labels: hours.map((h) => `${h}:00`),
    datasets: [
      {
        label: 'Requests by Hour',
        data: hours.map((h) => hourlyMap[h]?.requests || 0),
        backgroundColor: 'rgba(1, 105, 235, 0.6)',
        borderColor: CHART.primary,
        borderWidth: 2,
        borderRadius: 4,
      },
    ],
  };

  const breakdown = data?.costBreakdown || {};
  const costBreakdownChart = {
    labels: ['LLM Input', 'LLM Output', 'Audio'],
    datasets: [
      {
        data: [
          breakdown.llmInputCost || 0,
          breakdown.llmOutputCost || 0,
          breakdown.audioCost || 0,
        ],
        backgroundColor: [CHART.primary, CHART.purple, CHART.pink],
        borderWidth: 3,
        borderColor: '#fff',
      },
    ],
  };

  const seg = analytics?.userSegmentation || {};
  const segmentationChart = {
    labels: ['High Usage', 'Medium Usage', 'Low Usage'],
    datasets: [
      {
        label: 'User Count',
        data: [
          seg.highUsage?.count || 0,
          seg.mediumUsage?.count || 0,
          seg.lowUsage?.count || 0,
        ],
        backgroundColor: [CHART.bad, CHART.avg, CHART.good],
        borderWidth: 2,
        borderRadius: 8,
      },
    ],
  };

  const dist = analytics?.costDistribution || [];
  const distributionChart = {
    labels: dist.map((d) => d.range),
    datasets: [
      {
        label: 'Request Count',
        data: dist.map((d) => d.count),
        backgroundColor: 'rgba(118, 75, 162, 0.6)',
        borderColor: CHART.purple,
        borderWidth: 2,
        borderRadius: 6,
      },
    ],
  };

  const tokenChart = {
    labels: dailyData.map((d) => formatDate(d.date)),
    datasets: [
      {
        label: 'Input Tokens',
        data: dailyData.map((d) => d.inputTokens || 0),
        borderColor: CHART.primary,
        tension: 0.4,
        fill: false,
      },
      {
        label: 'Output Tokens',
        data: dailyData.map((d) => d.outputTokens || 0),
        borderColor: CHART.purple,
        tension: 0.4,
        fill: false,
      },
    ],
  };

  const tableRows = data?.detailedData || data?.dailyData || [];

  const usersAccum = {};
  tableRows.forEach((row) => {
    const userName = row.userName || row.user_name || 'Unknown User';
    const phoneNumber = row.phoneNumber || row.phone_number || 'N/A';
    const key = `${userName}-${phoneNumber}`;
    if (!usersAccum[key]) {
      usersAccum[key] = {
        userName,
        phoneNumber,
        requests: 0,
        inputTokens: 0,
        outputTokens: 0,
        audioDurationMinutes: 0,
        llmCost: 0,
        audioCost: 0,
        recipientDataCollectionCost: 0,
        topicDataCollectionCost: 0,
        testLifewinkCost: 0,
        receivedLifewinkCost: 0,
        totalCost: 0,
      };
    }
    const a = usersAccum[key];
    a.requests += row.requests || row.totalRequests || 0;
    a.inputTokens += row.inputTokens || 0;
    a.outputTokens += row.outputTokens || 0;
    a.audioDurationMinutes += parseFloat(row.audioDurationMinutes || 0);
    a.llmCost += row.llmCost || 0;
    a.audioCost += row.audioCost || 0;
    a.recipientDataCollectionCost += row.recipientDataCollectionCost || 0;
    a.topicDataCollectionCost += row.topicDataCollectionCost || 0;
    a.testLifewinkCost += row.testLifewinkCost || 0;
    a.receivedLifewinkCost += row.receivedLifewinkCost || 0;
    a.totalCost += row.cost || 0;
  });
  const sortedUsers = Object.values(usersAccum)
    .sort((a, b) => b.totalCost - a.totalCost)
    .slice(0, userLimit);

  const dailyAccum = {};
  tableRows.forEach((row) => {
    const date = row.date;
    if (!dailyAccum[date]) {
      dailyAccum[date] = {
        date,
        users: new Set(),
        requests: 0,
        inputTokens: 0,
        outputTokens: 0,
        audioDurationMinutes: 0,
        llmCost: 0,
        audioCost: 0,
        cost: 0,
      };
    }
    const a = dailyAccum[date];
    a.users.add(row.userName || row.userId || 'Unknown');
    a.requests += row.requests || row.totalRequests || 0;
    a.inputTokens += row.inputTokens || 0;
    a.outputTokens += row.outputTokens || 0;
    a.audioDurationMinutes += parseFloat(row.audioDurationMinutes || 0);
    a.llmCost += row.llmCost || 0;
    a.audioCost += row.audioCost || 0;
    a.cost += row.cost || 0;
  });
  const sortedDaily = Object.values(dailyAccum)
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 10);

  const rankClass = (rank) =>
    rank === 1 ? styles.rank1 : rank === 2 ? styles.rank2 : rank === 3 ? styles.rank3 : '';
  const toneClass = (tone) =>
    tone === 'high' ? styles.badgeHigh : tone === 'medium' ? styles.badgeMedium : styles.badgeLow;

  const stats = [
    { label: 'Total Cost', value: formatCurrency(summary.totalCost) },
    { label: 'Language Model Cost', value: formatCurrency(summary.totalLlmCost) },
    { label: 'Audio Cost', value: formatCurrency(summary.totalAudioCost) },
    { label: 'Recipient Data Collection', value: formatCurrency(summary.totalRecipientDataCollectionCost) },
    { label: 'Topic Data Collection', value: formatCurrency(summary.totalTopicDataCollectionCost) },
    { label: 'Test Your Lifewink', value: formatCurrency(summary.totalTestLifewinkCost) },
    { label: 'Received Lifewink', value: formatCurrency(summary.totalReceivedLifewinkCost) },
    { label: 'Total Requests', value: formatNumber(summary.totalRequests) },
    { label: 'Avg Cost/Request', value: formatCurrency(summary.averageCostPerRequest) },
    { label: 'Active Users', value: formatNumber(summary.uniqueUsers) },
    { label: 'Input Tokens', value: formatNumber(summary.totalInputTokens) },
    { label: 'Output Tokens', value: formatNumber(summary.totalOutputTokens) },
    { label: 'Audio Duration', value: `${formatNumber(summary.totalAudioMinutes)} min` },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1>AI Usage &amp; Cost</h1>
        <p>Monitor AI service usage and cost analysis.</p>
      </div>

      <DashboardFilters
        phone={phone}
        startDate={startDate}
        endDate={endDate}
        onPhoneChange={setPhone}
        onStartDate={setStartDate}
        onEndDate={setEndDate}
        onApply={loadDashboard}
        loading={loading}
      />

      {phone.trim() && data?.userName && (
        <div className={styles.userInfo}>
          <div>
            <div className={styles.label}>Name</div>
            <div className={styles.value}>{data.userName}</div>
          </div>
          <div>
            <div className={styles.label}>Phone</div>
            <div className={styles.value}>{data.phoneNumber || 'N/A'}</div>
          </div>
          <div>
            <div className={styles.label}>User ID</div>
            <div className={styles.value}>{data.userId || 'N/A'}</div>
          </div>
        </div>
      )}

      {error && <div className={styles.errorBanner}>{error}</div>}

      <DashboardTabs tabs={TABS} active={activeTab} onChange={setActiveTab} />

      {loading ? (
        <div className={styles.loaderWrap}>
          <Loader />
        </div>
      ) : (
        <>
          {activeTab === 'overview' && (
            <>
              <div className={styles.statsGrid}>
                {stats.map((s) => (
                  <StatCard key={s.label} label={s.label} value={s.value} />
                ))}
              </div>
              <div className={styles.chartsGrid}>
                <ChartCard title="Daily Cost Trend" wide>
                  <Line data={costTrend} options={baseOptions({ currency: true })} />
                </ChartCard>
              </div>
            </>
          )}
          {activeTab === 'analytics' && (
            <div className={styles.chartsGrid}>
              <ChartCard title="Hourly Usage Patterns">
                <Bar data={hourlyChart} options={baseOptions()} />
              </ChartCard>
              <ChartCard title="Cost Breakdown">
                <Doughnut data={costBreakdownChart} options={doughnutOptions()} />
              </ChartCard>
              <ChartCard title="User Segmentation">
                <Bar data={segmentationChart} options={baseOptions()} />
              </ChartCard>
              <ChartCard title="Cost Distribution">
                <Bar data={distributionChart} options={baseOptions()} />
              </ChartCard>
              <ChartCard title="Token Usage Analysis" wide>
                <Line data={tokenChart} options={baseOptions()} />
              </ChartCard>
            </div>
          )}

          <div className={styles.tableCard}>
            <div className={styles.tableHeader}>
              <span>User-wise Cost Analysis</span>
              <select
                className={styles.topSelect}
                value={userLimit}
                onChange={(e) => setUserLimit(Number(e.target.value))}
              >
                {[5, 10, 20, 50, 100].map((n) => (
                  <option key={n} value={n}>{`Top ${n}`}</option>
                ))}
              </select>
            </div>
            <div className={styles.tableScroll}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Rank</th><th>User</th><th>Phone</th><th>Requests</th>
                    <th>Input</th><th>Output</th><th>Audio (min)</th>
                    <th>LLM Cost</th><th>Audio Cost</th><th>Recipient</th>
                    <th>Topic</th><th>Test</th><th>Received</th>
                    <th>Total Cost</th><th>Avg/Req</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedUsers.length ? (
                    sortedUsers.map((u, i) => {
                      const rank = i + 1;
                      const avg = u.requests > 0 ? u.totalCost / u.requests : 0;
                      return (
                        <tr key={`${u.userName}-${u.phoneNumber}`}>
                          <td><span className={`${styles.rank} ${rankClass(rank)}`}>{rank}</span></td>
                          <td>{u.userName}</td>
                          <td>{u.phoneNumber}</td>
                          <td>{formatNumber(u.requests)}</td>
                          <td>{formatNumber(u.inputTokens)}</td>
                          <td>{formatNumber(u.outputTokens)}</td>
                          <td>{u.audioDurationMinutes.toFixed(1)}</td>
                          <td>{formatCurrency(u.llmCost)}</td>
                          <td>{formatCurrency(u.audioCost)}</td>
                          <td>{formatCurrency(u.recipientDataCollectionCost)}</td>
                          <td>{formatCurrency(u.topicDataCollectionCost)}</td>
                          <td>{formatCurrency(u.testLifewinkCost)}</td>
                          <td>{formatCurrency(u.receivedLifewinkCost)}</td>
                          <td><strong>{formatCurrency(u.totalCost)}</strong></td>
                          <td><span className={`${styles.badge} ${toneClass(costTone(avg))}`}>{formatCurrency(avg)}</span></td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr><td className={styles.empty} colSpan={15}>No data available.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className={styles.tableCard}>
            <div className={styles.tableHeader}><span>Daily Usage Summary</span></div>
            <div className={styles.tableScroll}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Date</th><th>Users</th><th>Requests</th><th>Input</th>
                    <th>Output</th><th>Audio (min)</th><th>LLM Cost</th>
                    <th>Audio Cost</th><th>Total Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedDaily.length ? (
                    sortedDaily.map((d) => (
                      <tr key={d.date}>
                        <td>{formatDate(d.date)}</td>
                        <td>{d.users.size}</td>
                        <td>{formatNumber(d.requests)}</td>
                        <td>{formatNumber(d.inputTokens)}</td>
                        <td>{formatNumber(d.outputTokens)}</td>
                        <td>{d.audioDurationMinutes.toFixed(1)}</td>
                        <td>{formatCurrency(d.llmCost)}</td>
                        <td>{formatCurrency(d.audioCost)}</td>
                        <td><strong>{formatCurrency(d.cost)}</strong></td>
                      </tr>
                    ))
                  ) : (
                    <tr><td className={styles.empty} colSpan={9}>No data available.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default CostTracker;
